import React, { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { CommonDialog, CommonDialogContent, CommonDialogTitle, ProgressDialog } from "../../../common/dialogs";
import AddRoleToRequestDialogTable from "./add-role-to-request-dialog-table";
import { systemGroupsService } from "../../../../services/system-groups-service";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../../../store";
import { addRolesToRequest, setError } from "../../../../reducers/administration/requestSlice";
import { requestsService } from "../../../../services/requests-service";
import useScrollPagination from "../../../common/hooks/useScrollPagination";
import SearchHelpDialogActions from "../../../common/search-helps/search-help-actions";
import "./styles.less";
import { RequestRole, GroupSystemRole, RoleOwner } from "../../../../types/brm/request-types";
import { nanoid } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";

type AddRoleToRequestDialogProps = {
    open: boolean;
    onClose: () => void;
    groupId: string;
};

const getGroupSystems = async () => {
    const { groups } = await systemGroupsService.getGroups();
    return groups;
};

const convertDialogRolesToRequestRoles = (roles: GroupSystemRole[]): RequestRole[] => {
    return roles.map((role: GroupSystemRole) => ({
        id: nanoid(),
        role: role.role,
        groupId: role.groupId,
        description: role.description,
        isComplex: role.isComplex,
        isNew: false,
        owner: role.owner,
        deputyOwner: role.deputyOwner,
        added: true,
        children: [],
    }));
};

const AddRoleToRequestDialog: React.FC<AddRoleToRequestDialogProps> = ({ open, onClose, groupId }) => {
    const intl = useIntl();
    const title = intl.formatMessage({ id: "add-role-to-request-dialog.title" });
    const [currentSystem, setCurrentSystem] = useState("");
    const [systemId, setSystemId] = useState("");
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
    const [searchString, setSearchString] = useState("");
    const [roleTags, setRoleTags] = useState<{ key: string; text: string }[]>([]);
    const rolesForView = useSelector((state: RootState) => state.administration.requests.rolesForView);
    const busyType = useSelector((state: RootState) => state.administration.requests.busyType);

    useEffect(() => {
        setRoleTags(
            selectedRoles.map((role) => {
                return {
                    key: role,
                    text: role,
                };
            })
        );
    }, [selectedRoles]);

    useEffect(() => {
        if (groupId.length > 0) {
            setCurrentSystem(groupId);
        }
    }, [groupId]);

    useEffect(() => {
        if (!currentSystem) {
            return;
        }

        if (!open) {
            return;
        }

        requestsService.getTargetSystemId(currentSystem).then((systemId) => setSystemId(systemId));
    }, [currentSystem, open]);

    const dispatch = useDispatch<AppDispatch>();

    const getData = useCallback(
        (page: number, signal: AbortSignal) => {
            if (currentSystem.length > 0 && open) {
                const params = {
                    page,
                    limit: 10,
                    search: searchString,
                };

                return requestsService.getExistedRoles(params, signal, systemId, currentSystem);
            }
        },
        [searchString, currentSystem, systemId, open]
    );

    const { items, setItems, busy, handleScroll } = useScrollPagination({ getData }) as unknown as {
        items: { id: string; description: string; isComplex: boolean; owner: RoleOwner; deputyOwner: RoleOwner; groupId: string; role: string }[];
        setItems: React.Dispatch<
            React.SetStateAction<{ id: string; description: string; isComplex: boolean; owner: RoleOwner; deputyOwner: RoleOwner; groupId: string; role: string }[]>
        >;
        busy: boolean;
        handleScroll: (event: React.UIEvent<HTMLDivElement>) => void;
    };

    useEffect(() => {
        if (open) {
            setItems([]);
        }
    }, [open, setItems]);

    const handleSaveClick = () => {
        const rolesForAdd = items.filter((role) => selectedRoles.includes(role?.id));

        const newRequestRoles = convertDialogRolesToRequestRoles(rolesForAdd);

        const existingRoleNames = new Set(rolesForView.roles.map((role) => role.role));
        const duplicateRoles = newRequestRoles.filter((role) => existingRoleNames.has(role.role));

        if (duplicateRoles.length > 0) {
            const duplicateRoleNames = duplicateRoles.map((role) => role.role).join(", ");
            dispatch(
                setError({
                    code: "VALIDATION_ERROR",
                    type: "error",
                    title: intl.formatMessage({ id: "common.validation-error" }),
                    detail: intl.formatMessage({ id: "add-role-to-request-dialog.error.roles-already-added" }, { duplicateRoleNames: duplicateRoleNames }),
                    errors: [],
                })
            );
            return;
        }

        dispatch(addRolesToRequest({ roles: newRequestRoles, groupId: currentSystem }));
        setSelectedRoles([]);
        onClose();
    };

    return (
        <CommonDialog className="add-role-to-request-dialog" open={open} maxWidth="lg" fullWidth >
            <CommonDialogTitle
                title={title}
                onClose={() => {
                    onClose();
                    setSelectedRoles([]);
                }}
                withoutCloseIcon={false}
            />

            <CommonDialogContent onScrollCapture={handleScroll} className="add-role-to-request-dialog-content">
                <AddRoleToRequestDialogTable
                    roles={items}
                    currentSystem={currentSystem}
                    setCurrentSystem={setCurrentSystem}
                    isSelectGroupDisabled={groupId.length > 0}
                    getGroupSystems={getGroupSystems}
                    selectedRoles={selectedRoles}
                    setSelectedRoles={setSelectedRoles}
                    searchString={searchString}
                    setSearchString={setSearchString}
                />
            </CommonDialogContent>

            <SearchHelpDialogActions
                onApply={handleSaveClick}
                onCancel={() => {
                    onClose();
                    setSelectedRoles([]);
                }}
                tags={roleTags}
                onDeleteTag={(key) => {
                    setSelectedRoles([...selectedRoles.filter((item) => item !== key)]);
                }}
                applyTitle={intl.formatMessage({ id: "add-role-to-request-dialog-actions.btn-add" })}
            />

            {busy && <ProgressDialog open={busy} busyType={busyType} title="" progress={""} />}

            {/* <AddRoleToRequestDialogActions
                onSave={handleSaveClick}
                onCancel={() => {
                    onClose();
                    setSelectedRoles([]);
                }}
            /> */}
        </CommonDialog>
    );
};

export default AddRoleToRequestDialog;
